/**
 * useCoinTicker — 선택한 코인 1종의 실시간 시세를 거래소 공개 WebSocket 에서 직접 수신.
 * 폴링 0 (우리 서버 미경유). 상장된 거래소만 연결, 미상장 거래소는 null 유지.
 * 코인 변경/언마운트 시 모든 소켓·재연결 타이머 정리 (메모리 누수 방지).
 *
 * - 업비트: wss://api.upbit.com/websocket/v1  (KRW-{COIN}, 바이너리 프레임 → text 디코드)
 * - 빗썸:   wss://pubwss.bithumb.com/pub/ws   ({COIN}_KRW, JSON)
 * - 바이낸스: wss://stream.binance.com:9443/ws/{coin}usdt@ticker (USDT 마켓, JSON)
 */
import { useEffect, useState } from "react";

export interface TickerPrice {
  price: number;
  change: number; // 비율 (0.01 = 1%)
}
export interface CoinTicker {
  upbit: TickerPrice | null;
  bithumb: TickerPrice | null;
  binance: TickerPrice | null;
}
interface Avail {
  u: boolean; // 업비트 상장
  b: boolean; // 빗썸 상장
  n: boolean; // 바이낸스(USDT) 상장
}

const EMPTY: CoinTicker = { upbit: null, bithumb: null, binance: null };

export function useCoinTicker(coin: string | null, avail: Avail | null): CoinTicker {
  const [ticker, setTicker] = useState<CoinTicker>(EMPTY);

  useEffect(() => {
    setTicker(EMPTY);
    if (!coin || !avail) return;

    let closed = false;
    const sockets: WebSocket[] = [];
    const timers: number[] = [];

    // 재연결 지원 커넥터. onMsg 는 closed 후 호출돼도 무시.
    const connect = (
      url: string,
      onOpen: (ws: WebSocket) => void,
      onMsg: (d: any) => void,
    ) => {
      const open = () => {
        if (closed) return;
        let ws: WebSocket;
        try {
          ws = new WebSocket(url);
        } catch {
          timers.push(window.setTimeout(open, 4000));
          return;
        }
        sockets.push(ws);
        ws.onopen = () => { if (!closed) onOpen(ws); };
        ws.onmessage = (ev) => {
          if (closed) return;
          const handle = (txt: string) => { if (!closed) { try { onMsg(JSON.parse(txt)); } catch { /* ignore */ } } };
          if (typeof ev.data === "string") handle(ev.data);
          else if (ev.data instanceof Blob) ev.data.text().then(handle).catch(() => { /* ignore */ });
        };
        ws.onclose = () => { if (!closed) timers.push(window.setTimeout(open, 4000)); };
        ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
      };
      open();
    };

    if (avail.u) {
      connect(
        "wss://api.upbit.com/websocket/v1",
        (ws) => ws.send(JSON.stringify([{ ticket: "trippang-coin" }, { type: "ticker", codes: [`KRW-${coin}`] }])),
        (d) => {
          if (d?.type === "ticker" && typeof d.trade_price === "number") {
            setTicker((p) => ({ ...p, upbit: { price: d.trade_price, change: Number(d.signed_change_rate) || 0 } }));
          }
        },
      );
    }
    if (avail.b) {
      connect(
        "wss://pubwss.bithumb.com/pub/ws",
        (ws) => ws.send(JSON.stringify({ type: "ticker", symbols: [`${coin}_KRW`], tickTypes: ["24H"] })),
        (d) => {
          if (d?.type === "ticker" && d.content?.closePrice != null) {
            setTicker((p) => ({ ...p, bithumb: { price: Number(d.content.closePrice), change: (Number(d.content.chgRate) || 0) / 100 } }));
          }
        },
      );
    }
    if (avail.n) {
      // 바이낸스는 코인/$(USDT 마켓). 단 USDT 자신은 USDTUSDT 쌍이 없어 → USDTUSD(USD 마켓) 사용.
      const binanceSym = coin.toUpperCase() === "USDT" ? "usdtusd" : `${coin.toLowerCase()}usdt`;
      connect(
        `wss://stream.binance.com:9443/ws/${binanceSym}@ticker`,
        () => { /* 구독 불필요 (URL 스트림) */ },
        (d) => {
          if (d?.c != null) {
            setTicker((p) => ({ ...p, binance: { price: Number(d.c), change: (Number(d.P) || 0) / 100 } }));
          }
        },
      );
    }

    return () => {
      closed = true;
      timers.forEach((id) => window.clearTimeout(id));
      sockets.forEach((ws) => { try { ws.close(); } catch { /* ignore */ } });
    };
    // 상장 플래그(primitive)만 의존 → 마켓 목록 객체가 재생성돼도 동일 코인이면 재연결 안 함
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coin, avail?.u, avail?.b, avail?.n]);

  return ticker;
}
